import React from "react";
import { Alert, Button } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { AttestationDay } from "../../../types/attestation";

interface IProps {
  /**
   * Every day of the current pay period still to work through, including ones blocked on mileage
   * — the run takes the BT to those too, since a blocked day now explains itself and can be
   * fixed in place. Days outside the period are deliberately not in here, marked though they are.
   */
  remainingDays: AttestationDay[];
  /**
   * Set when the day on screen is owed but no appointments came back for it, leaving nothing
   * to review and no way to move off it.
   */
  hasUnloadableDay?: boolean;
  /** Moves the run past a day whose appointments could not be loaded. */
  onSkipDay?: () => void;
  onExit: () => void;
}

/**
 * Shown when a day the server says is owed has no appointments on screen.
 *
 * Deliberately does not say the day is empty. A day with genuinely no work is never reported as
 * owed, so if this appears the appointments almost certainly exist and were simply fetched for a
 * window that does not match the clinic's day. It offers no way to attest either: the statement
 * a BT signs says the day is complete and accurate, and a blank day is not something to certify.
 */
const UnloadableDayNotice: React.FC<{ onSkipDay?: () => void }> = ({
  onSkipDay,
}) => (
  <Alert
    severity="warning"
    sx={{ mb: 2, borderRadius: "8px" }}
    data-testid="catchup-unloadable-day"
  >
    <span className="attestation-banner-title">
      This day&apos;s appointments couldn&apos;t be loaded
    </span>
    <span className="attestation-banner-detail">
      It can&apos;t be attested here yet, and it stays in your outstanding days.
      Move on for now and come back to it.
    </span>
    <div className="attestation-banner-action">
      <Button
        variant="contained"
        data-testid="catchup-skip-day"
        onClick={onSkipDay}
      >
        Skip for now
      </Button>
    </div>
  </Alert>
);

/**
 * Progress header shown while a BT works through their outstanding days, and the
 * completion state once nothing actionable is left. Shows days remaining rather than a
 * fixed "day X of Y" so the count stays truthful if a day is attested elsewhere mid-run.
 */
const AttestationCatchUp: React.FC<IProps> = ({
  remainingDays,
  hasUnloadableDay,
  onSkipDay,
  onExit,
}) => {
  if (remainingDays.length > 0) {
    const dayLabel = remainingDays.length === 1 ? "day" : "days";

    return (
      <>
        {hasUnloadableDay && <UnloadableDayNotice onSkipDay={onSkipDay} />}
        <div className="attestation-catchup-header" data-testid="catchup-header">
          {/*
            * The urgency lives on the count, not the whole line. A red sentence inside a calm
            * blue bar with a dismiss ✕ reads as a closeable FYI whatever its colour, so the
            * label carries the mode and the chip carries the deadline.
            */}
          <span className="attestation-catchup-label">
            Catching up
            <span className="attestation-catchup-count">
              {remainingDays.length} {dayLabel} left to attest
            </span>
          </span>
          <button
            type="button"
            className="attestation-catchup-exit"
            aria-label="Exit catch-up"
            data-testid="catchup-exit"
            onClick={onExit}
          >
            &#10005;
          </button>
        </div>
      </>
    );
  }

  /*
   * Reached only when nothing in the current pay period is outstanding. Blocked days are part of
   * the run now, so it cannot finish while one of this period's days is still owed — which means
   * this really is an all-clear rather than "nothing left that you happen to be able to do".
   *
   * A day owed outside the period does not hold this back, and the copy claims only the period:
   * such a day is still marked on the strip and still confirmable, but it is not work this
   * period's deadline is asking for.
   */
  return (
    <Alert
      severity="success"
      icon={<CheckCircleOutlineIcon />}
      sx={{
        mb: 2,
        backgroundColor: "#EDF7ED",
        color: "#1E4620",
        borderRadius: "8px",
        "& .MuiAlert-icon": { color: "#2E7D32" },
      }}
      className="attestation-catchup-complete"
      data-testid="catchup-complete"
    >
      <span className="attestation-banner-title">You&apos;re all caught up</span>
      <span className="attestation-banner-detail">
        Every day in this pay period is attested.
      </span>
      <div className="attestation-banner-action">
        <Button variant="contained" data-testid="catchup-done" onClick={onExit}>
          Back to my schedule
        </Button>
      </div>
    </Alert>
  );
};

export default AttestationCatchUp;
